import React, { useState, useEffect, useRef } from 'react';
import ServiceSelect from '../components/journey/ServiceSelect';
import AstrologerPanel from '../components/journey/AstrologerPanel';
import { API_BASE } from '../components/journey/PanelIcons';
import './AstrologerPage.css';

const AUTH_KEY    = 'vtd_auth';
const EXPIRY_KEY  = 'vtd_auth_expiry';
const AUTH_MS     = 7 * 24 * 60 * 60 * 1000; // 7 days

const readAuth = () => {
  try {
    const auth   = localStorage.getItem(AUTH_KEY);
    const expiry = localStorage.getItem(EXPIRY_KEY);
    return auth === 'authenticated' && expiry && Date.now() < parseInt(expiry, 10);
  } catch { return false; }
};

const writeAuth = () => {
  try {
    localStorage.setItem(AUTH_KEY,   'authenticated');
    localStorage.setItem(EXPIRY_KEY, String(Date.now() + AUTH_MS));
  } catch {}
};

const clearAuth = () => {
  try {
    localStorage.removeItem(AUTH_KEY);
    localStorage.removeItem(EXPIRY_KEY);
  } catch {}
};

const AstrologerPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [screen,          setScreen]          = useState('gate'); // 'gate' | 'select' | 'panel'
  const [input,           setInput]           = useState('');
  const [error,           setError]           = useState('');
  const [loginLoading,    setLoginLoading]    = useState(false);
  const [showPw,          setShowPw]          = useState(false);
  const [service,         setService]         = useState(null);
  const [clientData,      setClientData]      = useState(null);
  const [extraData,       setExtraData]       = useState(null);
  const deferredPrompt = useRef(null);

  // ── Check saved auth on mount ───────────────────────────────────────────────
  useEffect(() => {
    document.title = 'VTD Astrologer — AI Tool';
    if (readAuth()) {
      setIsAuthenticated(true);
      setScreen('select');
    } else {
      clearAuth();
      setIsAuthenticated(false);
      setScreen('gate');
    }
    return () => { document.title = 'VTD Astrology'; };
  }, []);

  // ── PWA install prompt ──────────────────────────────────────────────────────
  useEffect(() => {
    const handler = e => { e.preventDefault(); deferredPrompt.current = e; };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt.current) { window.open('/install-instructions.html', '_blank'); return; }
    deferredPrompt.current.prompt();
    const { outcome } = await deferredPrompt.current.userChoice;
    if (outcome === 'accepted') deferredPrompt.current = null;
  };

  // ── Login — password verified by backend, not client ───────────────────────
  const handleLogin = async e => {
    e.preventDefault();
    if (!input.trim()) return;
    setLoginLoading(true);
    setError('');
    try {
      const res  = await fetch(`${API_BASE}/api/auth/astrologer-login`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ password: input }),
      });
      const data = await res.json();
      if (data.success) {
        writeAuth();
        setIsAuthenticated(true);
        setScreen('select');
      } else {
        setError('Incorrect password. Please try again.');
        setInput('');
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  // ── Logout ──────────────────────────────────────────────────────────────────
  const handleLogout = () => {
    clearAuth();
    setIsAuthenticated(false);
    setScreen('gate');
    setService(null);
    setClientData(null);
    setExtraData(null);
  };

  const handleStart = (svc, client, extra) => {
    setService(svc);
    setClientData(client);
    setExtraData(extra || null);
    setScreen('panel');
  };

  const handleChangeService = () => {
    if (window.confirm('End current session and return to service selection?')) {
      setService(null);
      setClientData(null);
      setExtraData(null);
      setScreen('select');
    }
  };

  // ── Guard: never render inner screens if not authenticated ──────────────────
  if (!isAuthenticated) {
    return (
      <div className="astrologer-gate">
        <div className="astrologer-gate__card">
          <div className="astrologer-gate__logo">
            <GateSunIcon />
            <div className="astrologer-gate__logo-text">
              <span className="astrologer-gate__brand">VTD</span>
              <span className="astrologer-gate__brand-sub">Astrologer</span>
            </div>
          </div>

          <h1 className="astrologer-gate__title">AI Astrologer Tool</h1>
          <p className="astrologer-gate__sub">Restricted access — VTD practitioners only</p>

          <form onSubmit={handleLogin} className="astrologer-gate__form">
            <div className="astrologer-gate__field">
              <label htmlFor="pw-input" className="astrologer-gate__label">Password</label>
              <div className="astrologer-gate__input-wrap">
                <input
                  id="pw-input"
                  type={showPw ? 'text' : 'password'}
                  value={input}
                  onChange={e => { setInput(e.target.value); setError(''); }}
                  placeholder="Enter access password"
                  className={`astrologer-gate__input${error ? ' astrologer-gate__input--error' : ''}`}
                  autoComplete="current-password"
                  autoFocus
                  disabled={loginLoading}
                />
                <button type="button" className="astrologer-gate__eye"
                  onClick={() => setShowPw(v => !v)} aria-label="Toggle visibility">
                  {showPw ? '🙈' : '👁'}
                </button>
              </div>
              {error && <p className="astrologer-gate__error">{error}</p>}
            </div>
            <button type="submit" className="astrologer-gate__btn" disabled={!input.trim() || loginLoading}>
              {loginLoading ? 'Verifying...' : 'Enter →'}
            </button>
          </form>

          <p className="astrologer-gate__footer">
            © {new Date().getFullYear()} VTD Astrology · Restricted Tool
          </p>

          <button type="button" className="astrologer-gate__install-btn"
            onClick={handleInstall} title="Install as app on your phone">
            📱 Install as App
          </button>
        </div>
      </div>
    );
  }

  /* ── Panel screen ── */
  if (screen === 'panel') {
    return (
      <div className="astrologer-page">
        <AstrologerPanel
          service={service}
          clientData={clientData}
          initialExtraData={extraData}
          onChangeService={handleChangeService}
          onLogout={handleLogout}
        />
      </div>
    );
  }

  /* ── Service selection screen ── */
  return (
    <div className="astrologer-page">
      <ServiceSelect onStart={handleStart} onLogout={handleLogout} />
    </div>
  );
};

const GateSunIcon = () => (
  <svg viewBox="0 0 40 40" width="32" height="32" aria-hidden="true">
    <circle cx="20" cy="20" r="9" fill="#D4AF37" />
    <circle cx="20" cy="20" r="5" fill="#FFD700" />
    {[0,45,90,135,180,225,270,315].map((deg, i) => {
      const r = (deg * Math.PI) / 180;
      return <line key={i}
        x1={20 + Math.cos(r) * 12} y1={20 + Math.sin(r) * 12}
        x2={20 + Math.cos(r) * 17} y2={20 + Math.sin(r) * 17}
        stroke="#FFD700" strokeWidth="1.8" strokeLinecap="round" />;
    })}
  </svg>
);

export default AstrologerPage;
